// KS2 Teacher Guide — Junior Programme (Ages 7–11)
// Companion to the Student Book + Workbook. Each chapter contains:
//   - Lesson plan (60 min)
//   - Discussion prompts (3-4)
//   - Differentiation notes
//   - Cross-curricular links
//   - Assessment cues
//   - Safeguarding / sensitivity notes (where relevant)

export const META = {
  series: "Ambedkarite Buddhist Educational Series — UK Edition",
  title: "Junior Programme · Teacher Guide",
  subtitle: "Ages 7–11 · 12 lesson plans, discussion prompts and assessment cues",
  edition: "Phase 1 · Teacher Guide · 2026",
};

export const NOTES = [
  {
    n: 1, title: "What is Dignity?",
    aim: "Pupils explore what dignity means and how it is protected in modern Britain.",
    plan: [
      { mins: "0–5",  step: "Settle the class. Introduce the word 'dignity'. Take 3 spoken responses." },
      { mins: "5–15", step: "Read the story 'A Boy Who Loved to Learn' aloud. Pause for the water-tap moment." },
      { mins: "15–25", step: "Define dignity & equality (concept section). Use the vocabulary list. Add words to working wall." },
      { mins: "25–40", step: "The Dignity Wall activity (sticky notes — see Student Book Activity)." },
      { mins: "40–55", step: "Pair-share: 'one rule I'd change at school to make it kinder'." },
      { mins: "55–60", step: "Closing reflection — read 3 sticky notes aloud." },
    ],
    discuss: [
      "How do you think Bhimrao felt when he was refused water?",
      "Has anyone in our class felt left out before? (Volunteer-only — never compulsory.)",
      "Where does dignity 'live' in our school — in places, in people, or in rules?",
    ],
    differentiation: "Lower-attaining: provide sentence starters ('Dignity means…'). Higher-attaining: ask them to compare dignity to a different word (respect, fairness).",
    cross: "Citizenship · British Values (rule of law, individual liberty). PSHE · belonging.",
    assess: "Pupils can give one definition of dignity AND one example of dignity in school life.",
    safeguard: "Be sensitive to pupils who may have experienced exclusion or prejudice. Allow opt-in for personal sharing.",
  },
  {
    n: 2, title: "The Prince Who Sought Truth",
    aim: "Pupils retell the early life of Siddhartha Gautama and connect his Four Sights to questions of meaning.",
    plan: [
      { mins: "0–10",  step: "Recap dignity. Ask: 'What does it mean to seek truth?'" },
      { mins: "10–25", step: "Read the story of Siddhartha's Four Sights. Use illustrations if available." },
      { mins: "25–40", step: "Carousel of Big Questions: each child writes one 'why?' on a slip; group them on the wall." },
      { mins: "40–55", step: "Workbook tasks (drawing the bodhi tree). Music optional — Mettā chant." },
      { mins: "55–60", step: "Reflection — what mattered MORE to Siddhartha than gold?" },
    ],
    discuss: [
      "Can someone be rich AND unhappy at the same time?",
      "Why do we sometimes only learn by leaving familiar places?",
      "Is it brave or selfish to leave a comfortable life to find truth?",
    ],
    differentiation: "Use a story-board approach for SEND pupils. Stretch: introduce the word 'aspiration'.",
    cross: "RE — religious education survey of Buddhism. Literacy — narrative structure.",
    assess: "Pupils can sequence the Four Sights and explain why they changed Siddhartha.",
    safeguard: "If a pupil shares a real-life story of loss (linked to old age / sickness / death), respond gently and follow school protocol.",
  },
  {
    n: 3, title: "Compassion (Karuṇā) — When Hearts Help",
    aim: "Pupils define karuṇā and identify everyday acts of compassion at school.",
    plan: [
      { mins: "0–10", step: "Word of the day — write 'karuṇā' on the board. Have pupils say it together." },
      { mins: "10–20", step: "Read 'The Hare and the Hungry Stranger' story together." },
      { mins: "20–35", step: "Compassion Map activity: draw the school, mark places where karuṇā can happen." },
      { mins: "35–50", step: "Workbook tasks (matching, drawing two hands)." },
      { mins: "50–60", step: "Compassion pledge — each child writes one act they'll do this week." },
    ],
    discuss: [
      "Is compassion the same as feeling sorry for someone? Why or why not?",
      "Can compassion be shown to ourselves?",
      "What stops us from acting on compassion sometimes?",
    ],
    differentiation: "Lower: pair with a confident speaker for the matching task. Higher: write a compassion-focused short story.",
    cross: "PSHE — relationships. Literacy — animal-fable structure.",
    assess: "Pupils can give a definition + 2 specific school examples.",
  },
  {
    n: 4, title: "Mindfulness — A Quiet Mind",
    aim: "Pupils experience a 4-4-6 breathing exercise and reflect on their state of mind.",
    plan: [
      { mins: "0–5",  step: "Settle: 30 seconds of silence. Notice sounds." },
      { mins: "5–15", step: "Read the 'Mind as Weather' analogy from the Student Book." },
      { mins: "15–25", step: "Lead the 4-4-6 breath. Repeat three rounds. Stay calm and slow." },
      { mins: "25–40", step: "Mind-weather drawing in the workbook." },
      { mins: "40–55", step: "Pair-share: 'A place where my mind feels quiet.'" },
      { mins: "55–60", step: "Closing bell or chime if available." },
    ],
    discuss: [
      "When is your mind most stormy? When most calm?",
      "How does the body feel after slow breathing?",
      "Why do you think monks have practised this for 2,500 years?",
    ],
    differentiation: "For pupils uncomfortable closing eyes — invite a 'soft gaze' on a fixed point.",
    cross: "PE — body awareness. PSHE — self-regulation.",
    assess: "Pupils can explain the 4-4-6 breath technique to a partner.",
    safeguard: "Some pupils may find stillness triggering (anxiety, trauma). Always make eyes-closed optional and have a quiet exit.",
  },
  {
    n: 5, title: "The First School for Girls",
    aim: "Pupils learn about Savitribai and Jyotirao Phule and connect their bravery to access-to-education today.",
    plan: [
      { mins: "0–10",  step: "Show a portrait of Savitribai. Ask: 'What does her face tell you?'" },
      { mins: "10–25", step: "Read the chapter together. Pause when stones are thrown." },
      { mins: "25–40", step: "Hot-seat: a child plays Savitribai; others ask questions." },
      { mins: "40–55", step: "Workbook tasks — design a 'School of the Future'." },
      { mins: "55–60", step: "Closing — read out loud the names of women teachers in the school." },
    ],
    discuss: [
      "Why do you think people threw stones at Savitribai?",
      "What do we still take for granted today that some children in the world don't have?",
      "Who is a 'first' in YOUR family — first to do something brave?",
    ],
    differentiation: "Lower: use illustrated story-cards. Higher: research another female pioneer.",
    cross: "History — 19th-century reform. Citizenship — equality of access.",
    assess: "Pupils can summarise the impact of Savitribai's work in 2–3 sentences.",
  },
  {
    n: 6, title: "The Power of \"Why?\"",
    aim: "Pupils unpack Babasaheb's call to 'Educate, Agitate, Organise' as critical-thinking skills.",
    plan: [
      { mins: "0–10",  step: "Riddle round — pose 3 'why?' riddles." },
      { mins: "10–25", step: "Read the chapter. Highlight 'Educate, Agitate, Organise'." },
      { mins: "25–40", step: "Big Question Wall — each child writes a 'why?' about school or society." },
      { mins: "40–55", step: "Workbook tasks." },
      { mins: "55–60", step: "Vote on the Big Question of the Week to investigate next lesson." },
    ],
    discuss: [
      "Are there any questions we shouldn't ask? Why or why not?",
      "What's the difference between asking and arguing?",
      "When have you seen people 'organise' to change something?",
    ],
    differentiation: "Use Bloom's 'why/what if' question stems for stretch.",
    cross: "Citizenship — democracy. PSHE — speaking up.",
    assess: "Each pupil produces 3 well-formed 'why' questions.",
  },
  {
    n: 7, title: "A Constitution for Equals",
    aim: "Pupils understand what a constitution does and co-create one for their classroom.",
    plan: [
      { mins: "0–10",  step: "Show pupils Babasaheb's portrait & the 1950 Indian Constitution image." },
      { mins: "10–25", step: "Read the chapter. Pause to explain 'rule of law'." },
      { mins: "25–45", step: "In groups of 4, draft 3 rules for the classroom that are fair to everyone." },
      { mins: "45–55", step: "Whole-class vote on rules. Combine the top 5–7 into a Class Constitution." },
      { mins: "55–60", step: "Sign the constitution. Display it." },
    ],
    discuss: [
      "Why do communities need rules at all?",
      "When should a rule be changed?",
      "Is voting always fair? When isn't it?",
    ],
    differentiation: "Lower: provide sentence frames for rule-writing. Higher: research one article of the Indian Constitution.",
    cross: "Citizenship — rule of law, democracy. Literacy — formal writing.",
    assess: "Pupils can articulate 1 reason a constitution helps a community.",
  },
  {
    n: 8, title: "The Eightfold Path",
    aim: "Pupils learn the eight steps and apply each to a real school situation.",
    plan: [
      { mins: "0–10",  step: "Recap the words from previous lessons." },
      { mins: "10–25", step: "Walk-through the Eightfold Path on the working wall." },
      { mins: "25–45", step: "Path-stones activity: 8 stations around the classroom, each a step." },
      { mins: "45–55", step: "Workbook matching task." },
      { mins: "55–60", step: "Each pupil chooses one step to practise this week." },
    ],
    discuss: [
      "Which step is hardest for adults too?",
      "Is 'Right Speech' the same as 'always agreeing'?",
      "Can effort be wrong? When?",
    ],
    differentiation: "SEND — pair each step with an emoji. Stretch — write a 'how-to' guide for one step.",
    cross: "PSHE — moral reasoning. RE — Buddhist practice.",
    assess: "Pupils can name 5+ of the 8 steps and give one school example.",
  },
  {
    n: 9, title: "The Monkey King's Bridge",
    aim: "Pupils explore self-sacrifice and apply it to community responsibility.",
    plan: [
      { mins: "0–10",  step: "Tell the Jātaka tale of the Monkey King with expression." },
      { mins: "10–25", step: "Pause-and-predict — what would you have done?" },
      { mins: "25–40", step: "Bridge-building craft — using paper or LEGO, design a 'bridge' that helps others." },
      { mins: "40–55", step: "Workbook task." },
      { mins: "55–60", step: "Closing — share one bridge a pupil made for someone this week." },
    ],
    discuss: [
      "Was the Monkey King brave or foolish?",
      "Are there bridges we make every day at school without realising?",
      "Can sacrifice be small AND meaningful?",
    ],
    differentiation: "Lower — story-board the tale. Higher — write a modern version.",
    cross: "Literacy — fable. Design-tech — bridge-building.",
    assess: "Pupils can explain what 'sacrifice' means with one example.",
  },
  {
    n: 10, title: "Many Faiths, One Britain",
    aim: "Pupils celebrate the diversity of faith in the UK and identify shared values.",
    plan: [
      { mins: "0–10",  step: "Map of UK on board — pin festival images from different communities." },
      { mins: "10–25", step: "Read the chapter together." },
      { mins: "25–45", step: "Festival-share circle — each pupil shares a tradition (food / festival / song)." },
      { mins: "45–55", step: "Workbook task — draw the Tree of Friends." },
      { mins: "55–60", step: "Closing — sing or chant a peace verse together." },
    ],
    discuss: [
      "What's the difference between tolerance and respect?",
      "Why might shared values be more important than shared beliefs?",
      "Can a school have its own 'culture'?",
    ],
    differentiation: "EAL — invite pupils to share in their first language. Stretch — research a less-discussed faith locally.",
    cross: "RE — multi-faith literacy. Geography — UK demographics.",
    assess: "Pupils can name 3 faiths plus one shared value.",
    safeguard: "Avoid competition between faiths. Frame as celebration, not comparison.",
  },
  {
    n: 11, title: "The Kind Emperor",
    aim: "Pupils analyse Ashoka's transformation and identify qualities of compassionate leadership.",
    plan: [
      { mins: "0–10",  step: "Show an image of an Ashokan rock edict and the Ashoka Chakra." },
      { mins: "10–25", step: "Read the chapter aloud. Pause at the battle of Kalinga." },
      { mins: "25–45", step: "Class Leader Election — pupils write a 1-line manifesto of kindness." },
      { mins: "45–55", step: "Workbook task — design an Edict of Kindness poster." },
      { mins: "55–60", step: "Vote and display the winning poster." },
    ],
    discuss: [
      "What does it take for a powerful person to change?",
      "Can a country be remembered for kindness more than for war?",
      "What is one rule you'd carve into stone for all schools?",
    ],
    differentiation: "Lower — sentence stems for the manifesto. Higher — write a 30-word edict in plain English.",
    cross: "History — early Indian empires. Citizenship — leadership.",
    assess: "Pupils can describe Ashoka's change and identify one leadership quality.",
  },
  {
    n: 12, title: "We Are the Next Story",
    aim: "Pupils consolidate the year's learning and commit to one personal action.",
    plan: [
      { mins: "0–10",  step: "Recap journey — 12 chapters in 12 words on the board." },
      { mins: "10–25", step: "Gallery walk of pupils' best workbook pages." },
      { mins: "25–40", step: "Letter to my future self — pupils write to themselves in 5 years." },
      { mins: "40–55", step: "Workbook closing tasks." },
      { mins: "55–60", step: "Group bow — Sabbe sattā sukhitā hontu (\"may all beings be happy\")." },
    ],
    discuss: [
      "Which idea will you carry into KS3?",
      "What surprised you most this year?",
      "Who is the next person YOU want to teach what you learned?",
    ],
    differentiation: "Provide a 'memory map' template for pupils who struggle with open writing.",
    cross: "PSHE — self-reflection. Literacy — letter-writing.",
    assess: "Each pupil produces a personal pledge / letter as a portfolio piece.",
  },
];
